import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';

import { ConfigService } from './config.service';
import { Constants } from '../utilities/constants.util';
import { CostCenter } from '../models/cost-center.model';
import { InpatientConfiguration } from '../models/inpatient-configuration.model';
import { AncillaryConfiguration } from '../models/ancillary-configuration.model';

import * as moment from 'moment';
import { ShiftBlock } from '../models/shift-block.model';
import { ShiftRecord } from '../models/shift-record.model';

@Injectable({
  providedIn: 'root'
})
export class CostCenterService {

    readonly apiServer: string;

    constructor(private _configService: ConfigService, private _http: HttpClient) {
        this.apiServer = this._configService.appSettings.webApiServer + "api/CostCenter/";
    }

    async getCostCenter(id: number): Promise<CostCenter> {
        const params = new HttpParams()
            .set('costCenterId', id.toString());

        return await this._http.get<CostCenter>(`${this.apiServer}GetCostCenter`, { params: params }).toPromise<CostCenter>();
    }

    async getCostCenterForWorkflow(costCenterId: number, workflowTimeFacility: string, shiftBlock: ShiftBlock) {
        const params = new HttpParams()
            .set('costCenterId', costCenterId.toString())
            .set('workflowTimeFacility', moment(workflowTimeFacility).format(Constants.FacilityTimeFormat))
            .set('shiftBlockCreated', moment(shiftBlock.created).format(Constants.FacilityTimeFormat));

        return await this._http.get<CostCenter>(`${this.apiServer}GetCostCenterForWorkflow`, { params: params }).toPromise<CostCenter>();
    }

    async getPlannerCostCenter(): Promise<number> {
        return await this._http.get<number>(`${this.apiServer}GetPlannerCostCenter`).toPromise<number>();
    }

    async getCostCenters(
        mostRecent: boolean,
        offset: number,
        totalItems: number,
        searchText: string,
        orderBy: string,
        direction: string,
        activeOnly: boolean): Promise<CostCenter[]> {
        const params = new HttpParams()
            .set('mostRecent', mostRecent.toString())
            .set('offset', offset.toString())
            .set('totalItems', totalItems.toString())
            .set('searchText', searchText)
            .set('orderBy', orderBy)
            .set('direction', direction)
            .set('activeOnly', activeOnly.toString());

        return await this._http.get<CostCenter[]>(`${this.apiServer}GetCostCenters`, { params: params }).toPromise<CostCenter[]>();
    }

    async getCostCentersRemaining() {
      return await this._http.get<number>(`${this.apiServer}GetCostCentersRemaining`).toPromise<number>();
    }

    async getAllActiveCostCenters(): Promise<CostCenter[]> {
        const params = new HttpParams()
            .set('orderBy', 'Name')
            .set('direction', Constants.DirectionAscending)
            .set('activeOnly', 'true');

        return await this._http.get<CostCenter[]>(`${this.apiServer}GetCostCenters`, { params: params }).toPromise<CostCenter[]>();
    }

    async GetDehydratedCostCenters(costCenterType:string, ancillaryType:string): Promise<any> {
        const params = new HttpParams()
          .set('costCenterType', costCenterType)
          .set('ancillaryType', ancillaryType ? ancillaryType : '');

        return await this._http.get<any>(`${this.apiServer}GetDehydratedCostCenters`, { params: params }).toPromise<any>();
    }

    async removeCostCenter(id: number): Promise<boolean> {
        const params = new HttpParams()
            .set('Id', id.toString());

        return await this._http.delete<boolean>(`${this.apiServer}RemoveCostCenter`, { params: params }).toPromise<boolean>();
    }

    async setCostCenter(costCenter: CostCenter, forceShiftBlockInactivation: boolean): Promise<CostCenter> {
        const params = new HttpParams()
            .set('forceShiftBlockInactivation', forceShiftBlockInactivation.toString());

        return await this._http.post<CostCenter>(`${this.apiServer}SetCostCenter`, JSON.stringify(costCenter), { params : params }).toPromise<CostCenter>();
    }

    async setInpatientConfiguration(inpatientConfiguration: InpatientConfiguration) {
        return this._http.post<number>(`${this.apiServer}SetInpatientConfiguration`, inpatientConfiguration).toPromise();
    }

    async setAncillaryConfiguration(ancillaryConfiguration: AncillaryConfiguration) {
        return this._http.post<number>(`${this.apiServer}SetAncillaryConfiguration`, ancillaryConfiguration).toPromise();
    }

    async getFutureShiftRecordForCostCenter(costCenterId: number): Promise<ShiftRecord> {
        const params = new HttpParams().set('costCenterId', costCenterId.toString());

        return this._http.get<ShiftRecord>(`${this.apiServer}GetFutureShiftRecordForCostCenter`, { params : params }).toPromise<ShiftRecord>();
    }

    async copyCostCenter(costCenterId: number): Promise<CostCenter[]> {
        const params = new HttpParams().set('costCenterId', costCenterId.toString());
        return this._http.get<CostCenter[]>(`${this.apiServer}CopyCostCenter`, { params : params }).toPromise<CostCenter[]>();
    }
}
